import { IAuthContext } from "../contexts/authContext/AuthContext";
import { TRecordAny, TRecordString } from "../global.types";
import { isEvalaProduction, isLoginAppSubdomain } from "../global.utils";
import { ISessionData } from "../login/state/sessionSlice";
import { logger } from "./log";

abstract class Analytics {
    readonly ID: string;
    protected isLoaded = false;
    protected loadPromise: Promise<void> | null = null;

    constructor(ID: string) {
        this.ID = ID;
    }

    async initialize(userId: string): Promise<void> {
        throw new Error("Method 'initialize' must be implemented.");
    }

    async sendEvent(event: string, data?: TRecordString): Promise<void> {
        throw new Error("Method 'sendEvent' must be implemented.");
    }

    enableTracking(): void {
        // @ts-ignore
        // window["ga-disable-" + this.ID] = false;
    }

    /**
     * Maybe disables tracking
     * https://stackoverflow.com/a/26758245/3352544
     * */
    disableTracking(): void {
        // @ts-ignore
        // window["ga-disable-" + this.ID] = true;
    }
}

class ReactGTMWrapper extends Analytics {
    _hasUserConsent = false;

    async initialize(): Promise<void> {
        if (this.isLoaded) return Promise.resolve();
        if (this.loadPromise) return this.loadPromise;

        this.loadPromise = new Promise((resolve, reject) => {
            this.setupConsent("default", this._hasUserConsent ? "granted" : "denied");
            // eslint-disable-next-line custom-rules/no-new-date
            this.pushToDataLayer({'gtm.start': new Date().getTime(), event:'gtm.js'});
            const script = document.createElement('script');
            script.async = true;
            script.src = `https://www.googletagmanager.com/gtm.js?id=${this.ID}`;
            script.onload = () => {
                this.isLoaded = true;
                resolve();
            };
            script.onerror = reject;
            document.head.appendChild(script);
        });

        return this.loadPromise;
    }

    /**
     * When user log in or register, we can add user consent to GTM and start tracking him
     */
    enableUserConsent(): void {
        if (this._hasUserConsent) {
            return;
        }
        this._hasUserConsent = true;
        if (this.loadPromise) {
            // in case event has been already sent and default consent was set, we need to sent update event.
            // otherwise, we will send "default" consent event later, when first custom event will be triggered
            this.setupConsent("update", "granted");
        }
    }

    disabledUserConsent(): void {
        if (!this._hasUserConsent) {
            return;
        }
        this._hasUserConsent = false;
        if (this.loadPromise) {
            // in case event has been already sent and default consent was set, we need to sent update event.
            // otherwise, we will send "default" consent event later, when first custom event will be triggered
            this.setupConsent("update", "denied");
        }
    }

    pushToDataLayer(data: TRecordAny) {
        window.dataLayer = window.dataLayer || [];
        window.dataLayer.push(data);
    }

    setupConsent(mode: "default" | "update" = "default", status: "granted" | "denied" = "granted"): void {
        this.pushToDataLayer({
            "event": `${mode}_consent`,
            "consent": mode,
            "ad_storage": status,
            "analytics_storage": status,
            "ad_user_data": status,
            "ad_personalization": status
        });
    }

    async sendEvent(event: string, data?: TRecordString): Promise<void> {
        await this.initialize();
        this.enableTracking();
        this.pushToDataLayer({
            event,
            ...data,
        });
        this.disableTracking();
    }
}

// const ReactGA4WrapperInstance = new ReactGA4Wrapper();
const ReactGTMWrapperInstance = new ReactGTMWrapper("GTM-5TF563J");

type IGetSessionParams = { context: IAuthContext; sessionData?: never; UserGuid?: never; }
    | { context?: never; sessionData: ISessionData; UserGuid?: never; }
    | { UserGuid: string; context?: never; sessionData?: never };

export function getSessionGTMParams({ context, sessionData, UserGuid }: IGetSessionParams): TRecordString {
    const tenantId = sessionData?.TenantId ?? context?.tenantId;
    const userId = UserGuid ?? sessionData?.UserGuid ?? context?.userGuid;
    return { tenant_id: tenantId?.toString(), user_id: userId?.toString() };
}

export const fireGTMEvent = async (event: string, data?: TRecordString): Promise<void> => {
    if (isEvalaProduction() && isLoginAppSubdomain()) {
        try {
            await ReactGTMWrapperInstance.sendEvent(event, data);
        } catch (e) {
            logger.warn("GTM call failed", e);
        }
    } else {
        logger.debug("GTM event triggered - not allowed outside login page", null, { event, ...data });
    }
};

export function toggleGTMConsent(isEnabled: boolean): void {
    if (isEnabled) {
        ReactGTMWrapperInstance.enableUserConsent();
    } else {
        ReactGTMWrapperInstance.disabledUserConsent();
    }
}
